import { Controller } from "stimulus";

export default class extends Controller {
  static targets = [
    "dropZone",
    "fileInput",
    "selectedFile",
    "fileName",
    "fileSize",
    "submitBtn",
    "nameField",
    "statusText",
    "progressBar",
    "progressText",
  ];

  connect() {
    this.selectedFile = null;
    this.simulationId = null;
    this.pollingInterval = null;
    this.backgroundPolling = false; // Track if polling in background
  }

  openFileDialog(event) {
    // Don't open if clicking the remove button or other interactive elements
    if (event.target.closest("button")) {
      return;
    }
    this.fileInputTarget.click();
  }

  handleDragOver(event) {
    event.preventDefault();
    if (this.hasDropZoneTarget) {
      this.dropZoneTarget.classList.add("drag-over");
    }
  }

  handleDragLeave(event) {
    event.preventDefault();
    if (this.hasDropZoneTarget) {
      this.dropZoneTarget.classList.remove("drag-over");
    }
  }

  handleDrop(event) {
    event.preventDefault();
    console.log("handleDrop called", event);

    if (this.hasDropZoneTarget) {
      this.dropZoneTarget.classList.remove("drag-over");
    }

    const files = event.dataTransfer.files;
    console.log("Files from drop:", files);

    if (files && files.length > 0) {
      this.selectFile(files[0]);
    }
  }

  handleFileSelect(event) {
    const files = event.target.files;
    if (files.length > 0) {
      this.selectFile(files[0]);
    }
  }

  selectFile(file) {
    console.log("selectFile called with:", file.name, file.type, file.size);

    // Validate file type
    const validTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];
    const validExtensions = [".pdf", ".doc", ".docx"];
    const fileExt = file.name
      .substring(file.name.lastIndexOf("."))
      .toLowerCase();

    if (!validTypes.includes(file.type) && !validExtensions.includes(fileExt)) {
      alert("Please upload a PDF, DOC, or DOCX file");
      return;
    }

    // Validate file size (50MB max)
    if (file.size > 50 * 1024 * 1024) {
      alert("File size must be less than 50MB");
      return;
    }

    this.selectedFile = file;

    // Display selected file
    console.log("Setting file name:", file.name);
    this.fileNameTarget.textContent = file.name;
    this.fileSizeTarget.textContent = `(${this.formatFileSize(file.size)})`;
    this.selectedFileTarget.style.display = "block";

    console.log("File selected, enabling submit button");

    // Enable submit button
    this.submitBtnTarget.disabled = false;
  }

  clearFile(event) {
    event.preventDefault();
    this.selectedFile = null;
    this.selectedFileTarget.style.display = "none";
    this.fileInputTarget.value = "";
    this.submitBtnTarget.disabled = true;
  }

  async handleSubmit(event) {
    event.preventDefault();

    if (!this.selectedFile) {
      alert("Please select a file to upload");
      return;
    }

    const name = this.nameFieldTarget.value.trim();
    if (!name) {
      alert("Please enter a scenario name");
      return;
    }

    try {
      this.submitBtnTarget.disabled = true;
      this.showProcessingModal();
      this.updateProgress(5, "Creating simulation...");

      // Create simulation first
      const simulation = await this.createSimulation();
      this.simulationId = simulation.id;

      this.updateProgress(15, "Requesting upload URL...");

      // Get presigned URL
      const presignData = await this.getPresignedUrl(simulation.id);

      this.updateProgress(25, "Uploading document...");

      // Upload to S3
      await this.uploadToS3(presignData);

      this.updateProgress(50, "Submitting for AI processing...");

      // Notify backend
      await this.submitUploadedKey(simulation.id, presignData.key);

      this.updateProgress(60, "AI is analyzing your document...");

      // Start polling for completion
      await this.pollForCompletion(simulation.id);
    } catch (error) {
      console.error("Upload error:", error);
      this.hideProcessingModal();
      alert(`Upload failed: ${error.message}`);
      this.submitBtnTarget.disabled = false;
    }
  }

  async createSimulation() {
    const formData = new FormData(document.getElementById("upload-form"));

    const response = await fetch("/simulations", {
      method: "POST",
      headers: {
        "X-CSRF-Token": document.querySelector('[name="csrf-token"]').content,
        Accept: "application/json",
      },
      body: formData,
    });

    if (!response.ok) {
      throw new Error("Failed to create simulation");
    }

    return await response.json();
  }

  async getPresignedUrl(simulationId) {
    const response = await fetch(
      `/simulations/${simulationId}/presign_s3_upload`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('[name="csrf-token"]').content,
        },
        body: JSON.stringify({
          filename: this.selectedFile.name,
          content_type: this.selectedFile.type,
        }),
      }
    );

    if (!response.ok) {
      throw new Error("Failed to get upload URL");
    }

    return await response.json();
  }

  async uploadToS3(presignData) {
    const response = await fetch(presignData.url, {
      method: "PUT",
      headers: {
        "Content-Type": this.selectedFile.type,
      },
      body: this.selectedFile,
    });

    if (!response.ok) {
      throw new Error("S3 upload failed");
    }
  }

  async submitUploadedKey(simulationId, s3Key) {
    const response = await fetch(
      `/simulations/${simulationId}/submit_uploaded_key`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('[name="csrf-token"]').content,
        },
        body: JSON.stringify({
          s3_key: s3Key,
          content_type: this.selectedFile.type,
        }),
      }
    );

    if (!response.ok) {
      throw new Error("Failed to submit upload");
    }

    return await response.json();
  }

  async pollForCompletion(simulationId) {
    let attempts = 0;
    const maxAttempts = 120; // 10 minutes max (5 second intervals)
    let lastProgress = 60;

    return new Promise((resolve, reject) => {
      this.pollingInterval = setInterval(async () => {
        attempts++;

        try {
          const response = await fetch(
            `/simulations/${simulationId}/upload_status`
          );
          const data = await response.json();

          // Better status messages based on processing stage
          let progress = 60;
          let message = "";

          if (attempts < 4) {
            // First 15 seconds - converting document
            progress = 60 + attempts * 2;
            message = "Converting document to PDF...";
          } else if (attempts < 8) {
            // Next 20 seconds - extracting text
            progress = 68 + (attempts - 4);
            message = "Extracting text and tables...";
          } else if (data.has_demographics) {
            progress = 75;
            message = "Demographics extracted...";
          } else if (data.has_history) {
            progress = 80;
            message = "Medical history extracted...";
          } else if (data.has_vitals > 0) {
            progress = 85 + Math.min(data.has_vitals, 10);
            message = `Processing vital signs (${data.has_vitals} found)...`;
          } else {
            // Still processing, increment slowly
            progress = Math.min(lastProgress + 1, 85);
            message = `AI analyzing document (${attempts * 5}s elapsed)...`;
          }

          lastProgress = progress;

          // Only update UI if modal is still visible
          if (!this.backgroundPolling) {
            this.updateProgress(progress, message);
          }

          if (data.processing_complete) {
            clearInterval(this.pollingInterval);

            if (this.backgroundPolling) {
              // Processing completed in background - show notification and redirect
              this.showCompletionNotification(simulationId);
            } else {
              // Modal still visible - show completion and redirect
              this.updateProgress(100, "Complete! Redirecting...");
              setTimeout(() => {
                this.hideProcessingModal();
                window.location.href = `/simulations/${simulationId}`;
              }, 800);
            }

            resolve(data);
          } else if (attempts >= maxAttempts) {
            clearInterval(this.pollingInterval);

            if (this.backgroundPolling) {
              // Timeout in background - show notification
              alert(
                `Scenario processing is taking longer than expected. The scenario has been created at /simulations/${simulationId} and is still being processed.`
              );
            } else {
              this.updateProgress(
                95,
                "Processing is taking longer than expected..."
              );

              // Offer to view the simulation anyway
              if (
                confirm(
                  `AI processing is taking longer than expected. The simulation has been created and is still being processed in the background.\n\nWould you like to view it now? (You can refresh later to see the AI-extracted data)`
                )
              ) {
                this.hideProcessingModal();
                window.location.href = `/simulations/${simulationId}`;
              } else {
                this.hideProcessingModal();
                window.location.href = `/simulations`;
              }
            }

            resolve(data);
          }
        } catch (error) {
          console.error("Polling error:", error);
        }
      }, 5000); // Poll every 5 seconds
    });
  }

  showProcessingModal() {
    this.backgroundPolling = false;
    $("#processing-modal").modal("show");

    // Listen for modal dismissal to enable background polling
    $("#processing-modal").on("hidden.bs.modal", () => {
      if (this.pollingInterval) {
        // Modal was dismissed but polling is still active
        this.backgroundPolling = true;
        console.log("Modal dismissed - continuing to poll in background");
      }
    });
  }

  hideProcessingModal() {
    $("#processing-modal").modal("hide");
    if (this.pollingInterval && !this.backgroundPolling) {
      // Only stop polling if explicitly hiding (not from user dismissal)
      clearInterval(this.pollingInterval);
    }
  }

  showCompletionNotification(simulationId) {
    // Show a success notification and redirect
    if (
      confirm(
        "🎉 Your scenario has been processed successfully!\n\nClick OK to view it now."
      )
    ) {
      window.location.href = `/simulations/${simulationId}`;
    } else {
      // User chose not to view - show a more subtle alert
      const alertDiv = document.createElement("div");
      alertDiv.className = "alert alert-success alert-dismissible fade in";
      alertDiv.style.cssText =
        "position: fixed; top: 70px; right: 20px; z-index: 9999; min-width: 300px;";
      alertDiv.innerHTML = `
        <button type="button" class="close" data-dismiss="alert" aria-label="Close">
          <span aria-hidden="true">&times;</span>
        </button>
        <strong>Scenario Ready!</strong><br>
        <a href="/simulations/${simulationId}" class="alert-link">Click here to view</a>
      `;
      document.body.appendChild(alertDiv);

      // Auto-remove after 10 seconds
      setTimeout(() => {
        if (alertDiv.parentNode) {
          alertDiv.remove();
        }
      }, 10000);
    }
  }

  updateProgress(percent, message) {
    this.statusTextTarget.textContent = message;
    this.progressBarTarget.style.width = `${percent}%`;
    this.progressTextTarget.textContent = `${percent}%`;
  }

  formatFileSize(bytes) {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  }
}
